import styled from 'styled-components';
import { ThemeType } from '../../types/Theme';

/*
 * As classes daqui são todas prefixadas com `banner-`. A coluna do catálogo
 * (.container-conteudo) define utilitárias genéricas — `.linha` é um divisor de
 * 2px de altura — e nomes soltos como "linha" ou "titulo" acabam herdando elas.
 */
const BannersShowcaseContainer = styled.div<{theme: ThemeType}>`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    width: 100%;
    /* reserva espaço embaixo para a lista não encostar na borda da coluna */
    height: auto;
    margin-bottom: 8rem;
    text-align: left;
    color: ${({theme}) => theme.colors.tertiary};

    .banner-secao {
        font-weight: bold;
        width: 100%;
    }

    /* A tabela é a do painel (TableMainContainer). Aqui só o que é específico
       do banner: a alça de arraste, a miniatura e a coluna de status. */
    .banner-tabela {
        .th-wrapper { cursor: default; }

        tr.aberto > td { font-weight: bold; }

        .banner-alca {
            display: flex;
            align-items: center;
            cursor: grab;
            touch-action: none;

            &:active { cursor: grabbing; }
        }

        .banner-midia {
            cursor: pointer;
            width: 14rem;

            img {
                display: block;
                width: 100%;
                max-height: 4rem;
                object-fit: contain;
                object-position: left center;
                border-radius: 4px;
            }

            .banner-sem-arte {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                height: 4rem;
                border: 2px dashed ${({theme}) => theme.colors.elements};
                border-radius: 4px;
            }
        }

        .banner-direcionamento {
            cursor: pointer;
            white-space: normal;
        }

        .banner-status {
            display: flex;
            align-items: center;
        }
    }

    /* Mesmo divisor de 2px usado na coluna do catálogo (.linha), só que com o
       nome prefixado para não herdar as regras de largura de lá. */
    .banner-divisor {
        width: 100%;
        height: 2px;
        background-color: ${({theme}) => theme.colors.elements};
        border-radius: 50px;
    }

    .banner-erro { color: ${({theme}) => theme.colors.brandDanger}; }

    .banner-bloqueado {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 6rem;
        border-radius: 8px;
        background-color: ${({theme}) => theme.colors.white};
    }

    /* Formulário segue o fluxo da coluna, sem cartão em volta. */
    .banner-formulario {
        display: flex;
        flex-direction: column;
        gap: 1.6rem;
        width: 100%;

        .banner-grupo {
            display: flex;
            flex-direction: column;
            gap: 0.8rem;
            width: 100%;
        }

        .banner-titulo { font-weight: bold; }

        .banner-artes {
            display: flex;
            gap: 1.6rem;
            width: 100%;

            .banner-arte {
                display: flex;
                flex: 1 0 0;
                min-width: 0;
                flex-direction: column;
                gap: 0.8rem;

                .banner-arte-titulo {
                    display: flex;
                    flex-direction: column;
                }

                .banner-upload { display: contents; }

                /* Mesmo dropzone da aba Mídias do produto: os dois ícones sobrepostos
                   (galeria e play) e o texto ao lado. Só o tamanho é menor. */
                .banner-dropzone {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 1rem;
                    width: 100%;
                    height: 15rem;
                    padding: 1rem 1.6rem;
                    border-radius: 2rem;
                    cursor: pointer;
                    text-align: center;
                    background-color: ${({theme}) => theme.colors.white};

                    img {
                        width: 100%;
                        height: 100%;
                        border-radius: 20px;
                        object-fit: contain;
                    }

                    .icones {
                            position: relative;
                            flex-shrink: 0;
                            width: 10rem;
                            height: 100%;
                            margin-right: 4rem;

                        .galery-icon,
                        .play-icon {
                            position: absolute;
                            top: 0;
                            bottom: 0;
                            margin: auto 0;
                            width: 100%;
                            transition: 0.3s ease-in-out;
                        }

                        .galery-icon {
                            z-index: 2;
                            transform: scale(0.5) translateY(-10%) translateX(-20%) rotate(355deg);
                        }

                        .galery-ativo {
                            transform: scale(0.5) translateY(-15%) translateX(-35%) rotate(345deg);
                        }

                        .play-icon {
                            z-index: 2;
                            background-color: ${({theme}) => theme.colors.white};
                            transform: scale(0.5) translateY(10%) translateX(20%) rotate(5deg);
                        }

                        .play-ativo {
                            transform: scale(0.5) translateY(15%) translateX(35%) rotate(15deg);
                        }
                    }

                    .texto {
                        color: ${({theme}) => theme.colors.tertiary};
                    }
                }
            }
        }

        .banner-opcoes {
            display: flex;
            flex-direction: column;
            gap: 1rem;

            .banner-opcao {
                display: flex;
                align-items: center;
                gap: 1rem;
                cursor: pointer;

                input {
                    width: 1.8rem;
                    height: 1.8rem;
                    margin: 0;
                    accent-color: ${({theme}) => theme.colors.secondary};
                    cursor: pointer;
                }

                &.desabilitada {
                    cursor: not-allowed;

                    span { opacity: 0.5; }

                    input {
                        cursor: not-allowed;
                        opacity: 0.5;
                    }
                }

                .popover-direcionamento::after {
                    white-space: normal;
                    width: 50rem;
                    text-align: left;
                    border-radius: 0.8rem;
                }
            }
        }

        .banner-selects {
            display: flex;
            gap: 1.6rem;
            width: 100%;

            > * { flex: 1 0 0; min-width: 0; }

            /* O formulário do banner fica no pé da página: a lista aberta para
               baixo cairia por cima do rodapé fixo. Só aqui ela abre para cima.
               A âncora é a altura do campo (.select tem height: 6rem), e não
               bottom: 100% — este último mede a partir do topo do componente
               inteiro e deixaria o rótulo como um vão entre a lista e o campo. */
            .view-select .option.ativo {
                top: auto;
                bottom: calc(6rem + 0.4rem);
            }
        }

        .banner-acoes {
            display: flex;
            gap: 1rem;
            width: 100%;
            margin-top: 3rem;
            margin-bottom: 3rem;

            > * { flex: 1 0 0; min-width: 0; }
        }
    }
`;

export {
    BannersShowcaseContainer,
};
